import { alpha, SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system/styleFunctionSx';
import type { CSSProperties } from 'react';

type TopMenuStyle = SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>);

export const frostedPanelBg = (theme: Theme, opacity = 0.85) =>
  alpha(theme.palette.background.paper, opacity);

/** Rounded segment tabs (Insights distance tabs, Media Manager file types). */
export const frostedSegmentTabsSx = (theme: Theme): SxProps<Theme> => ({
  minHeight: 0,
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-list': {
    minHeight: 0,
    alignItems: 'flex-end',
  },
  '& .MuiTab-root': {
    minHeight: 0,
    py: 1.25,
    fontSize: theme.typography.body1.fontSize,
    textTransform: 'none',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    bgcolor: frostedPanelBg(theme, 0.35),
    color: 'text.primary',
    transition: 'background-color 0.2s ease',
    mx: 0.5,
    mb: 0,
  },
  '& .MuiTab-root.Mui-selected': {
    bgcolor: frostedPanelBg(theme, 0.85),
    fontWeight: 600,
  },
  '& .MuiTab-root.Mui-disabled': {
    opacity: 0.45,
  },
});

export const frostedPanelSx = (opacity = 0.85) => ({
  backgroundColor: (theme: Theme) => frostedPanelBg(theme, opacity),
  backdropFilter: 'blur(3px)',
});

/** Hero text inset — lighter tint + stronger blur, aligned with the public top menu. */
export const frostedHeroTextSx = (opacity = 0.35) => ({
  backgroundColor: (theme: Theme) => frostedPanelBg(theme, opacity),
  backdropFilter: 'blur(5px)',
});

/** Dual-halo text shadow for public top menu labels on variable backgrounds. */
const topMenuTextShadow = (theme: Theme): string => {
  const whiteHalo = alpha(theme.palette.common.white, 0.95);
  const whiteGlow = alpha(theme.palette.common.white, 0.55);
  const darkLayer = alpha(theme.palette.text.primary, 0.12);
  return `0 0 1px ${whiteHalo}, 0 1px 4px ${whiteGlow}, 0 1px 2px ${darkLayer}`;
};

/** Frosted glass background for the sticky public top menu bar. */
export const topMenuBarSx: TopMenuStyle = theme => ({
  backgroundColor: frostedPanelBg(theme, 0.65),
  backdropFilter: 'blur(5px)',
  boxShadow: `0 1px 0 ${theme.palette.divider}`,
});

/** Base styles for top menu nav buttons and inline labels. */
export const topMenuItemSx: TopMenuStyle = theme => ({
  mx: 4,
  my: 2,
  color: 'text.primary',
  textShadow: topMenuTextShadow(theme),
  borderRadius: 0,
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'text.secondary',
  },
});

/** Active page indicator for top menu nav items. */
export const topMenuActiveSx: TopMenuStyle = {
  borderBottom: '3px solid',
  borderColor: 'primary.main',
};

/** Drop-shadow for top menu logo and utility icons. */
export const topMenuIconSx: TopMenuStyle = theme => ({
  color: 'text.primary',
  filter: [
    `drop-shadow(0 0 1px ${alpha(theme.palette.common.white, 0.95)})`,
    `drop-shadow(0 1px 3px ${alpha(theme.palette.common.white, 0.5)})`,
    `drop-shadow(0 1px 1px ${alpha(theme.palette.text.primary, 0.15)})`,
  ].join(' '),
});

/** Capsule frosted background for inline labels (countdown units, weekday, etc.). */
export const frostedPillCss = (theme: Theme, opacity = 0.35): CSSProperties => ({
  display: 'inline-block',
  borderRadius: 9999,
  padding: '4px 12px',
  backgroundColor: frostedPanelBg(theme, opacity),
  backdropFilter: 'blur(5px)',
});

/** Clip a frosted overlay to a logo image's alpha channel. */
export const frostedLogoMaskImageSx = (src: string) => ({
  maskImage: `url("${src}")`,
  WebkitMaskImage: `url("${src}")`,
  maskSize: 'contain',
  WebkitMaskSize: 'contain',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskPosition: 'center',
  maskMode: 'alpha',
  WebkitMaskMode: 'alpha',
});
